import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { fetchProjectTasksMetrics } from 'api/projectTasks';
import axios from 'axios';
import { RootState, ThunkApi } from 'state/store';
import { TasksMetrics } from 'types/store/projectTasksList';
import { SLICE_STATUS } from 'utils/constants';
import { selectProjectTasksExportState } from '../export/exportSlice';
import { selectProjectTasksTableFilters } from '../projectTasksListSlice';
import { getTasksMetricsUrl, deserializeTasksMetrics } from './utils';
import { Status } from 'utils/customTypes';

interface TaskMetricsState {
  status?: Status;
  error?: string;
  value?: TasksMetrics;
}

const initialState: TaskMetricsState = {};

export interface FetchTasksMetricsParams {
  projectId?: string;
}

function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().projectTasksList.tasksMetrics;
  return status !== SLICE_STATUS.LOADING;
}

export const fetchTasksMetrics = createAsyncThunk<
  TasksMetrics,
  FetchTasksMetricsParams,
  ThunkApi
>(
  'projectTasksList/tasksMetrics/fetchTasksMetrics',
  async (params: FetchTasksMetricsParams, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const exportState = selectProjectTasksExportState(state);
      const url = getTasksMetricsUrl({
        projectId: params.projectId,
        filters: selectProjectTasksTableFilters(state),
        exportState: {
          selectAll: exportState.selectAllTasks,
          excludedIds: exportState.excludedTasksIds,
          selectedIds: exportState.selectedTasksIds,
        },
      });
      const response = await fetchProjectTasksMetrics(url);
      const deserializedResponse = await deserializeTasksMetrics(response);
      return deserializedResponse;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching tasks metrics');
    }
  },
  {
    condition: concurrencyCondition,
  }
);

const taskMetricsSlice = createSlice({
  name: 'projectTasksList/tasksMetrics',
  initialState,
  reducers: {
    resetTasksMetrics: (state) => {
      state.status = undefined;
      state.error = undefined;
      state.value = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTasksMetrics.pending, (state) => {
      state.status = SLICE_STATUS.LOADING;
    });
    builder.addCase(fetchTasksMetrics.fulfilled, (state, action) => {
      state.status = SLICE_STATUS.SUCCESS;
      state.value = action.payload;
    });
    builder.addCase(fetchTasksMetrics.rejected, (state, action) => {
      state.status = SLICE_STATUS.FAILED;
      state.error = action.error.message;
    });
  },
});

export const selectTasksMetricsStatus = (state: RootState) =>
  state.projectTasksList.tasksMetrics.status;

export const selectTasksMetrics = (state: RootState) =>
  state.projectTasksList.tasksMetrics.value;

export const selectEditableTasksCount = createSelector(
  [selectTasksMetrics],
  (taskMetrics) => taskMetrics?.editableCount
);

export const { resetTasksMetrics } = taskMetricsSlice.actions;

export default taskMetricsSlice.reducer;
